import { useState, useCallback } from "react";
import { uploadAsset } from "../../services/assetsApi";
import {
  Box,
  FileUpload,
  Icon,
  IconButton,
  Image,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { MdAdd } from "react-icons/md";
import { useParams } from "react-router-dom";
import { useRoomStore, usePlacedModelsStore, useUserInterfaceStore, useModelStore } from "../../store";
import { textureCache } from "../../services/textureCache";
import { getDecorationPlacementForCamera } from "../../constants/Helpers";
import { track, Events } from "../../utils/analytics";

export default function ImageUploadAndPreview({
  selectedValue,
  setSelectedValue,
}) {
  const { roomId } = useParams();

  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedAsset, setUploadedAsset] = useState(null);

  // Get room dimensions for default decoration placement
  const scaleX = useRoomStore((s) => s.scaleX);
  const scaleZ = useRoomStore((s) => s.scaleZ);
  const height = useRoomStore((s) => s.height);
  const addDecoration = useRoomStore((s) => s.addDecoration);
  const setSelectedDecoration = useUserInterfaceStore((s) => s.setSelectedDecoration);
  const cameraControls = useModelStore((s) => s.cameraControls);

  const handleFileChange = useCallback(
    async (file) => {
      setUploading(true);
      setPreview(null);
      setUploadedAsset(null);

      console.log(
        "Starting upload for file:",
        file.name,
        "Type:",
        file.type,
        "Size:",
        file.size
      );

      const result = await uploadAsset(file, { roomId });
      setUploading(false);

      if (result.success) {
        console.log("Upload success:", result.data);
        setUploadedAsset(result.data);

        // Set preview URL for images - try to load the image to verify it's accessible
        if (file.type.startsWith("image/")) {
          const imageUrl = result.data.publicUrl || result.data.url;

          if (!imageUrl) {
            console.warn(
              "Upload succeeded but no image URL provided:",
              result.data
            );
            return;
          }

          console.log("Setting image URL:", imageUrl);

          // Also set preview for the UI
          setPreview(imageUrl);

          // Load the image to get actual dimensions, then create decoration
          const img = document.createElement("img");
          img.onload = async () => {
            console.log("✅ Image loaded, dimensions:", img.width, "x", img.height);

            const imageOption = {
              url: imageUrl,
              width: img.width,
              height: img.height,
              color: "#ffffff",
            };

            // Set the decoration option for future manual placements
            if (setSelectedValue && typeof setSelectedValue === "function") {
              setSelectedValue(imageOption);
            }

            // Preload the texture into Three.js cache before creating decoration
            // This ensures the decoration doesn't appear as a white plane
            try {
              console.log("🔄 Preloading Three.js texture...");
              await textureCache.load(imageUrl);
              console.log("✅ Three.js texture loaded");
            } catch (textureError) {
              console.warn("⚠️ Failed to preload texture, creating decoration anyway:", textureError);
            }

            // Calculate default size - 20 units wide, proportional height
            const defaultWidth = 20;
            const aspectRatio = img.width / img.height;
            const defaultHeight = defaultWidth / aspectRatio;

            // Add random offset so multiple uploads don't stack exactly
            const randomX = (Math.random() - 0.5) * 30; // -15 to +15 units
            const randomY = (Math.random() - 0.5) * 20; // -10 to +10 units

            // Place on the wall the camera is currently facing
            const placement = getDecorationPlacementForCamera(
              cameraControls, scaleX, scaleZ, height, randomX, randomY
            );

            const decoration = {
              type: "image",
              option: imageOption,
              position: placement.position,
              rotation: placement.rotation,
              width: defaultWidth,
              height: defaultHeight,
              link: "",
              hoverText: "",
            };

            const currentDecorationsImage = useRoomStore.getState().decorations;
            const isFirstDecorationImage = currentDecorationsImage.length === 0;
            const roomIdImage = usePlacedModelsStore.getState()._roomId;

            console.log("🖼️ Auto-creating image decoration:", decoration);
            addDecoration(decoration);

            if (isFirstDecorationImage) {
              track(Events.DECORATION_ADDED, { roomId: roomIdImage, decorationType: 'image' });
            }

            // Select the newly created decoration (it will be at the end of the array)
            // Use setTimeout to ensure the decoration is added to the store first
            setTimeout(() => {
              const newIndex = useRoomStore.getState().decorations.length - 1;
              console.log("🎯 Selecting new decoration at index:", newIndex);
              setSelectedDecoration(decoration, newIndex);
            }, 50);
          };

          img.onerror = (error) => {
            console.warn(
              "⚠️ Image uploaded but may not be publicly accessible yet:",
              imageUrl,
              error
            );
            // Still set the value so user can try to place manually
            if (setSelectedValue && typeof setSelectedValue === "function") {
              setSelectedValue({
                url: imageUrl,
                width: 1024,
                height: 1024,
                color: "#ffffff",
              });
            }
          };

          // Only set src if imageUrl is valid
          if (imageUrl && imageUrl !== "null" && imageUrl !== "undefined") {
            img.src = imageUrl;
          } else {
            console.error("Invalid image URL:", imageUrl);
          }
        }
      } else {
        console.error("Upload failed:", result.error);
        alert("Upload failed: " + (result.error?.message || "Unknown error"));
      }
    },
    [roomId, setSelectedValue, scaleX, scaleZ, height, addDecoration, setSelectedDecoration, cameraControls]
  );
  return (
    <Box my="10px" background="#1C1C1C">
      <FileUpload.Root
        display="flex"
        alignItems="center"
        accept={[
          "image/png",
          "image/jpeg",
          "image/webp",
          "audio/mpeg",
          "audio/wav",
          "video/mp4",
        ]}
        maxFiles={1}
        onFileChange={(files) => {
          const file = files.acceptedFiles?.[0];
          if (file) handleFileChange(file);
        }}
      >
        <FileUpload.HiddenInput />
        <FileUpload.Trigger asChild>
          <IconButton
            style={{
              width: "40px",
              height: "35px",
              margin: "5px",
              alignItems: "center",
            }}
            background="#707070"
          >
            <Icon color="black">
              {uploading ? <Spinner size="sm" mr={2} /> : <MdAdd />}
            </Icon>
          </IconButton>
        </FileUpload.Trigger>
        {/* <FileUpload.List /> */}
      </FileUpload.Root>
      {preview || uploadedAsset || selectedValue?.url ? (
        <>
          <Box>
            <Text fontSize="sm" mb={2}>
              {preview ? "Preview:" : "Uploaded Asset:"}
            </Text>
            {preview ? (
              <Image
                src={preview}
                alt="Uploaded preview"
                maxW="200px"
                borderRadius="md"
                boxShadow="md"
              />
            ) : uploadedAsset ? (
              <Box p={2} bg="gray.100" borderRadius="md" maxW="200px">
                <Text fontSize="xs" fontWeight="bold">
                  {uploadedAsset.filename}
                </Text>
                <Text fontSize="xs" color="gray.600">
                  {uploadedAsset.contentType} •{" "}
                  {Math.round(uploadedAsset.fileSize / 1024)}KB
                </Text>
                {uploadedAsset.publicUrl && (
                  <Text fontSize="xs" color="blue.500" mt={1}>
                    <a
                      href={uploadedAsset.publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Asset
                    </a>
                  </Text>
                )}
              </Box>
            ) : selectedValue?.url ? (
              <Image
                src={selectedValue?.url}
                alt="Uploaded preview"
                maxW="200px"
                borderRadius="md"
                boxShadow="md"
                // background={"red"}
                // minWidth={"100px"}
                // minHeight={"100px"}
              />
            ) : null}
          </Box>
        </>
      ) : (
        <Text fontSize="sm" color="gray.500">
          No file uploaded
        </Text>
      )}
    </Box>
  );
}
